import type { FeatureFlagsSimplifi } from 'app-components/flux/feature-flags/featureFlagsSimplifi';
import type { PreferenceV2Name } from 'app-components/flux/preferences-v2/preferencesV2Names';
import events, { type MixPanelEventsType } from 'app-components/utils/core/tracker/events';
import type { AILauncherIconName } from 'components/AIComponents/AILauncher';

export type AiContextualPromptChipsOffLocation = 'settings' | 'chip-menu';

type TrackerEventName = MixPanelEventsType[keyof MixPanelEventsType];

export type QuickenAssistToggleDefinition = {
  id: string;
  iconName: AILauncherIconName;
  title: string;
  description: string;
  preferenceKey: PreferenceV2Name;
  /** When set, the row is only shown when this LaunchDarkly flag is enabled. */
  rolloutFlag?: keyof FeatureFlagsSimplifi;
  /** When true, the row is disabled unless the master Quicken AI toggle is on. */
  requiresMasterEnabled?: boolean;
  trackEnable?: TrackerEventName;
  trackDisable?: TrackerEventName;
  /** Sent with `trackDisable` (e.g. `AIContextualPromptChipsOff`). */
  trackDisableLocation?: AiContextualPromptChipsOffLocation;
  disableConfirm?: {
    title: string;
    content: string;
    confirmLabel: string;
  };
};

export const QUICKEN_ASSIST_MASTER_TOGGLE_ID = 'quicken-assist-enabled';
export const QUICKEN_ASSIST_CONTEXTUAL_CHIPS_TOGGLE_ID = 'quicken-assist-contextual-chips';
export const QUICKEN_ASSIST_WEEKLY_REPORT_TOGGLE_ID = 'quicken-assist-weekly-report';

export const QUICKEN_ASSIST_AI_DISCLAIMER =
  'AI-generated content may occasionally include errors, omissions, or inaccuracies. You remain solely responsible for verifying the accuracy of any information prior to reliance thereon.';

/**
 * Quicken AI settings rows. Add entries here when a user-facing preference exists
 * for an AI sub-feature (rollout flags alone are not surfaced as toggles).
 *
 * Candidate future rows (LD-only today): aiSummarize, aiChatBtnTopbar, investmentsWhy.
 */
export const QUICKEN_ASSIST_TOGGLES: QuickenAssistToggleDefinition[] = [
  {
    id: QUICKEN_ASSIST_MASTER_TOGGLE_ID,
    iconName: 'summarize-star',
    title: 'Enable Quicken AI',
    description: 'Turn on AI-powered chat, insights, summaries, and features across your entire financial space.',
    preferenceKey: 'aiInsightsOptIn',
    trackEnable: events.AIOn,
    trackDisable: events.AIOff,
    disableConfirm: {
      title: 'Disable Quicken AI',
      content:
        'When disabled, Quicken AI and its features will not be accessible across your entire financial space in Quicken Simplifi. You can re-enable it anytime.',
      confirmLabel: 'Confirm',
    },
  },
  {
    id: QUICKEN_ASSIST_CONTEXTUAL_CHIPS_TOGGLE_ID,
    iconName: 'message-star',
    title: 'Show contextual chips',
    description: 'Display suggested prompts on features such as Net Worth and Investments. (more coming soon)',
    preferenceKey: 'aiContextualPromptChipsEnabled',
    rolloutFlag: 'aiContextualPromptChips',
    requiresMasterEnabled: true,
    trackEnable: events.AIContextualPromptChipsOn,
    trackDisable: events.AIContextualPromptChipsOff,
    trackDisableLocation: 'settings',
    disableConfirm: {
      title: 'Hide suggested prompts?',
      content: 'This will hide suggested prompts everywhere in Simplifi. You can turn them back on here anytime.',
      confirmLabel: 'Hide',
    },
  },
  {
    id: QUICKEN_ASSIST_WEEKLY_REPORT_TOGGLE_ID,
    iconName: 'summarize-star',
    title: 'AI insights in your weekly report',
    description:
      'Add short AI-written observations to each section of your weekly report. Your report always shows your numbers, trends, and advice either way.',
    preferenceKey: 'aiWeeklyReportEnabled',
    rolloutFlag: 'aiWhatsNewRelease',
    requiresMasterEnabled: true,
    trackEnable: events.AIWeeklyReportOn,
    trackDisable: events.AIWeeklyReportOff,
    disableConfirm: {
      title: 'Turn off AI insights in your weekly report?',
      content:
        'Your weekly report will still show your spending, income, investments, and advice — just without the AI-written commentary. You can turn it back on here anytime.',
      confirmLabel: 'Turn off',
    },
  },
];
